package com.eladfinish.bible.contest.screens.openQuestions.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.copied_to_clipboard
import com.eladfinish.bible.contest.data.common.MyClipboardManager
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.extensions.applyAnswerMarkup
import com.eladfinish.bible.contest.extensions.applyQuestionMarkup
import com.eladfinish.bible.contest.extensions.makeBoldByAsterisksAnnotated
import com.eladfinish.bible.contest.navigation.NavigationCommand
import com.eladfinish.bible.contest.navigation.OpenQuestionsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.screenKey
import com.eladfinish.bible.contest.screens.chapterComplete.features.shuffle.RevealCardData
import com.eladfinish.bible.contest.screens.chapterComplete.features.shuffle.ui.ExpandableRevealCard
import com.eladfinish.bible.contest.screens.openQuestions.OpenQuestionsAction
import com.eladfinish.bible.contest.screens.openQuestions.OpenQuestionsEffect
import com.eladfinish.bible.contest.screens.openQuestions.OpenQuestionsState
import com.eladfinish.bible.contest.screens.openQuestions.OpenQuestionsStore
import com.eladfinish.bible.contest.screens.openQuestions.model.QuestionItem
import com.eladfinish.bible.contest.screens.openQuestions.model.TopicItem
import com.eladfinish.bible.contest.screens.openQuestions.ui.dialogs.OpenQuestionsDialogs
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.common.SetScreenIsLoading
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.ui.menus.actions.OpenQuestionsToolbarAction
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import dev.icerock.moko.resources.StringResource
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun OpenQuestionsScreen(
    isMultipleRandomVersesMode: Boolean = false,
    store: OpenQuestionsStore = koinViewModel(
        parameters = { parametersOf(isMultipleRandomVersesMode) }
    ),
    toolbarActionBus: ToolbarActionBusViewModel,
    showToast: (String) -> Unit,
    showToastRes: (StringResource) -> Unit,
    navigator: (NavigationCommand.Navigate) -> Unit = {},
) {

    val state by store.observeState().collectAsState()
    val listState = rememberLazyListState()
    val coroutineScope = rememberCoroutineScope()
    val clipboardManager: MyClipboardManager = getKoinInstance()

    LaunchedEffect(Unit) {
        store.dispatch(OpenQuestionsAction.Initialize)
    }

    LaunchedEffect(Unit) {
        store.observeSideEffect().collect { effect ->
            when (effect) {
                is OpenQuestionsEffect.ShowToast -> showToast(effect.message)
                is OpenQuestionsEffect.ShowToastRes -> showToastRes(effect.message)
                is OpenQuestionsEffect.ShowNoInternetError -> showToast("אין חיבור לאינטרנט")
                OpenQuestionsEffect.ScrollToTop -> coroutineScope.launch {
                    listState.animateScrollToItem(0)
                }

                is OpenQuestionsEffect.CopyToClipboard -> {
                    clipboardManager.copyToClipboard(effect.text)
                    showToastRes(SharedRes.strings.copied_to_clipboard)
                }

                is OpenQuestionsEffect.NavigateToSearch -> navigator(effect.navigation)
            }
        }
    }

    OpenQuestionsToolbarBindings(
        toolbarActionBus = toolbarActionBus,
        onAction = store::dispatch,
        isMultipleRandomVersesMode = state.isMultipleRandomVersesMode,
    )

    OpenQuestionsContent(
        state = state,
        listState = listState,
        onAction = store::dispatch,
    )

    OpenQuestionsDialogs(
        dialogState = state.dialogState,
        onAction = store::dispatch,
        onDismiss = { store.dispatch(OpenQuestionsAction.HideDialog) },
        onItemClicked = { index ->
            val customUrl = state.pendingCustomSheetUrl
            val linkEntries = state.pendingLinkEntries
            val specialType = state.pendingSpecialTopicType
            val topic = state.currentTopic
            when {
                // Special link-topic book picker (Numbers / Education Ministry)
                linkEntries.isNotEmpty() && specialType != null -> {
                    linkEntries.getOrNull(index)?.let { entry ->
                        store.dispatch(OpenQuestionsAction.LinkEntrySelected(entry, specialType))
                    }
                }

                // Custom user sheet picker
                customUrl.isNotEmpty() ->
                    store.dispatch(OpenQuestionsAction.CustomSheetPositionSelected(customUrl, index))

                // Sub-sheet picker for topics with predefined items
                topic != null && topic.items.isNotEmpty() -> {
                    val sheetNumbers = state.sheetNumbers
                    if (index in sheetNumbers.indices) {
                        store.dispatch(
                            OpenQuestionsAction.SheetNumberSelected(
                                sheetId = sheetNumbers[index].second,
                                topicName = sheetNumbers[index].second,
                            )
                        )
                    }
                }

                // Main topics list
                else ->
                    store.dispatch(OpenQuestionsAction.TopicFromDialogSelected(index))
            }
        },
        onChapterDialogTitleClick = { /* navigate to browse — future enhancement */ },
    )

    SetScreenIsLoading(state.isLoading)
}

@Composable
private fun OpenQuestionsToolbarBindings(
    toolbarActionBus: ToolbarActionBusViewModel,
    onAction: (OpenQuestionsAction) -> Unit,
    isMultipleRandomVersesMode: Boolean,
) {
    val screenKey = OpenQuestionsScreenNavigationDestination::class.screenKey
    val onDispatch by rememberUpdatedState(onAction)

    LaunchedEffect(screenKey) {
        toolbarActionBus.setActiveScreenKey(screenKey)
        toolbarActionBus.events.collect { event ->
            if (event.screenKey != screenKey) return@collect
            val toolbarAction = event.action as? OpenQuestionsToolbarAction ?: return@collect
            when (toolbarAction) {
                OpenQuestionsToolbarAction.MoreQuestions -> {
                    onDispatch(OpenQuestionsAction.LoadMoreQuestions)
                }

                OpenQuestionsToolbarAction.TableOfContents -> {
                    // In random-verses mode "Table of Contents" re-generates a new cocktail;
                    // in regular mode it shows the topics picker
                    if (isMultipleRandomVersesMode) {
                        onDispatch(OpenQuestionsAction.Initialize)
                    } else {
                        onDispatch(OpenQuestionsAction.LoadTopics)
                    }
                }

                OpenQuestionsToolbarAction.Help -> {
                    onDispatch(OpenQuestionsAction.HelpClicked)
                }

                OpenQuestionsToolbarAction.Refresh -> {
                    onDispatch(OpenQuestionsAction.RefreshQuestions)
                }

                OpenQuestionsToolbarAction.AddQuestion -> {
                    onDispatch(OpenQuestionsAction.AddQuestion)
                }

                OpenQuestionsToolbarAction.CreateYourOwnQnA -> {
                    onDispatch(OpenQuestionsAction.CreateYourOwnQnA)
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun OpenQuestionsContent(
    state: OpenQuestionsState,
    listState: LazyListState,
    onAction: (OpenQuestionsAction) -> Unit,
) {
    PullToRefreshBox(
        isRefreshing = state.isLoading,
        onRefresh = { onAction(OpenQuestionsAction.ReloadShownQuestions) },
    ) {
        if (state.questionItems.isNotEmpty() && !state.isLoading) {
            QuestionsListContent(
                state = state,
                listState = listState,
                onAction = onAction,
            )
        }
        // While topics are loading or no topic is selected yet, the content area
        // stays empty — the topics picker is shown via OpenQuestionsTableOfContents dialog.
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun QuestionsListContent(
    state: OpenQuestionsState,
    listState: LazyListState,
    onAction: (OpenQuestionsAction) -> Unit,
) {
    var titleVisible by rememberSaveable { mutableStateOf(true) }

    Column(modifier = Modifier.fillMaxSize()) {
        AnimatedVisibility(
            visible = titleVisible && state.topicTitle.isNotEmpty(),
            exit = shrinkVertically() + fadeOut(),
        ) {
            Text(
                text = state.topicTitle.makeBoldByAsterisksAnnotated(),
                modifier = Modifier
                    .fillMaxWidth()
                    .combinedClickable(
                        onClick = {},
                        onLongClick = { titleVisible = false },
                    )
                    .padding(horizontal = 16.dp, vertical = 8.dp),
                style = MaterialTheme.typography.titleLarge,
                textAlign = TextAlign.Center,
            )
        }

        LazyColumn(
            state = listState,
            modifier = Modifier
                .weight(1f)
                .mouseVerticalScrollbar(listState),
            contentPadding = navigationBarContentPadding(),
        ) {
            itemsIndexed(state.questionItems) { index, item ->
                QuestionItemWithContextMenu(
                    item = item,
                    index = index,
                    listState = listState,
                    onAction = onAction,
                    showChip = !state.isMultipleRandomVersesMode,
                )
            }
        }
    }
}

@Composable
private fun QuestionItemWithContextMenu(
    item: QuestionItem,
    index: Int,
    listState: LazyListState,
    onAction: (OpenQuestionsAction) -> Unit,
    showChip: Boolean = true,
) {
    var menuExpanded by rememberSaveable { mutableStateOf(false) }

    // Box anchors the DropdownMenu to this item instead of the top of the screen
    Box {
        ExpandableRevealCard(
            index = index + 1,
            revealCardData = item.toRevealCardData(appendSourceLabel = showChip),
            isExpanded = item.isAnswerVisible,
            onToggle = { onAction(OpenQuestionsAction.ToggleAnswerVisibility(index)) },
            onLongClick = { menuExpanded = true },
            listState = listState,
            listIndex = index,
            chipText = if (showChip) item.chipLabel else "",
        )

        DropdownMenu(
            expanded = menuExpanded,
            onDismissRequest = { menuExpanded = false },
        ) {
            DropdownMenuItem(
                text = { Text("העתק שאלה ${item.num}") },
                onClick = {
                    menuExpanded = false
                    onAction(OpenQuestionsAction.CopyQuestion(index))
                },
            )
            if (item.hasSource) {
                DropdownMenuItem(
                    text = { Text("הצג פרק") },
                    onClick = {
                        menuExpanded = false
                        onAction(OpenQuestionsAction.ShowChapterForQuestion(index))
                    },
                )
            }
            if (item.hasRegex) {
                DropdownMenuItem(
                    text = { Text("חפש ביטוי") },
                    onClick = {
                        menuExpanded = false
                        onAction(OpenQuestionsAction.SearchRegexForQuestion(index))
                    },
                )
            }
        }
    }
}

// ─── QuestionItemV2 → RevealCardData mapping ──────────────────────────────────
// Applies the same styling chain as CustomListAdapterBase.setDataInViewHolder:
//   <big>/<small> relative size · <b> bold · *bold* · _+  serif · author italic

private fun QuestionItem.toRevealCardData(appendSourceLabel: Boolean = true): RevealCardData =
    RevealCardData(
        otPasuk = chapter,
        reysha = question.applyQuestionMarkup(),
        seyfa = buildString {
            append(answer)
            if (appendSourceLabel && sourceLabel.isNotEmpty()) {
                append("  $sourceLabel")
            }
        }.applyAnswerMarkup(author = author),
    )

// ─── Previews ────────────────────────────────────────────────────────────────

private val previewEmptyState = OpenQuestionsState(
    allQuestions = persistentListOf(),
    topicItemList = persistentListOf(),
    predefinedTopics = persistentListOf(),
    sheetNumbers = persistentListOf(),
    dialogState = OpenQuestionsState.DialogState.None,
)

private val previewTopics = listOf(
    TopicItem(topic = "נביאים ראשונים", isPersonal = false, index = 1, id = "abc1", items = emptyList()),
    TopicItem(topic = "תורה — בראשית", isPersonal = false, index = 2, id = "abc2", items = emptyList()),
    TopicItem(topic = "מלכים ונביאים", isPersonal = false, index = 3, id = "abc3", items = emptyList()),
    TopicItem(topic = "חגים ומועדים", isPersonal = false, index = 4, id = "abc4", items = emptyList()),
)

private val previewQuestions = (1..12).map { i ->
    QuestionItem(
        question = "מה שמו של ה${if (i % 2 == 0) "מלך" else "נביא"} שמלך ב${if (i % 3 == 0) "יהודה" else "ישראל"} לאחר שלמה? (שאלה $i)",
        answer = "תשובה לשאלה $i",
        author = if (i % 4 == 0) "ישראל ישראלי" else "",
        book = if (i % 3 == 0) "מלכים א" else "",
        chapter = if (i % 3 == 0) "${i + 1}" else "",
        regex = if (i % 5 == 0) "regex_$i" else "",
        isAnswerVisible = i % 3 == 0,
        num = i,
    )
}

@Preview(showBackground = true, name = "Topics List — empty")
@Composable
private fun OpenQuestionsContentTopicsEmptyPreview() {
    PreviewTheme {
        OpenQuestionsContent(
            state = previewEmptyState,
            listState = rememberLazyListState(),
            onAction = {},
        )
    }
}

@Preview(showBackground = true, name = "Topics List — populated")
@Composable
private fun OpenQuestionsContentTopicsPopulatedPreview() {
    PreviewTheme {
        OpenQuestionsContent(
            state = previewEmptyState.copy(
                topicItemList = previewTopics.toImmutableList(),
            ),
            listState = rememberLazyListState(),
            onAction = {},
        )
    }
}

@Preview(showBackground = true, name = "Questions — all shown")
@Composable
private fun OpenQuestionsContentQuestionsPreview() {
    PreviewTheme {
        OpenQuestionsContent(
            state = previewEmptyState.copy(
                questionItems = previewQuestions.toImmutableList(),
                topicTitle = "נושא: *נביאים ראשונים* (12 שאלות)\n ענו על השאלות",
                questionsShownCount = previewQuestions.size,
            ),
            listState = rememberLazyListState(),
            onAction = {},
        )
    }
}
